import { z } from 'zod';
import { TENANT_MODULE_CATALOGUE, TENANT_MODULE_KEYS, } from './tenant-modules.js';
import { TESTER_EMAIL_DOMAIN } from './provisioning.js';
/**
 * "New test hospital" — Control builds a fresh SenseSys evaluation hospital for
 * one colleague, with the modules the owner ticks, and gives them working,
 * proven logins for every role (their own Super Admin login included).
 *
 * The node builds it exactly as an approved public signup would, so the
 * colleague tests what a customer gets. The id Control chooses becomes the
 * hospital's id on the tenant too — one id on both sides from the first
 * moment, and a retried command resumes the same hospital rather than building
 * a second one.
 */
/**
 * The tenant's module vocabulary as its signup form offers it.
 *
 * Mirrors `SIGNUP_MANDATORY_MODULES`, `SIGNUP_DEFAULT_ON_MODULES` and
 * `SIGNUP_HIDDEN_MODULES` in hospital-HRMS `constants/signup-modules.ts`. The
 * hidden ones are commercial entitlements (and ABDM, granted externally): the
 * node refuses them from any signup-shaped payload, so offering them here
 * would be a tick box that silently does nothing.
 */
export const TEST_HOSPITAL_MANDATORY_MODULES = [
    'patients',
    'basicBilling',
    'hrms',
    'basicReports',
];
export const TEST_HOSPITAL_DEFAULT_ON_MODULES = [
    'appointments',
    'opd',
    'prescriptions',
    'labOrders',
    'radiologyOrders',
    'pharmacy',
    'ipd',
    'emergency',
    'insuranceClaims',
    'walkinTokens',
    'infectionControl',
    'clinicalForms',
    'smsNotifications',
    'attendance',
    'leaveManagement',
    'patientPortal',
];
export const TEST_HOSPITAL_HIDDEN_MODULES = [
    'apiAccess',
    'advancedAnalytics',
    'commandCentre',
    'customIntegrations',
    'whiteLabel',
    'prioritySupport',
    'advancedReports',
    'abdmHealthId',
];
const HIDDEN = new Set(TEST_HOSPITAL_HIDDEN_MODULES);
const MANDATORY = new Set(TEST_HOSPITAL_MANDATORY_MODULES);
const DEFAULT_ON = new Set(TEST_HOSPITAL_DEFAULT_ON_MODULES);
/** What the "New test hospital" form shows, in catalogue order. */
export const TEST_HOSPITAL_MODULE_CHOICES = TENANT_MODULE_CATALOGUE.filter((m) => !HIDDEN.has(m.key)).map((m) => ({
    ...m,
    mandatory: MANDATORY.has(m.key),
    defaultOn: MANDATORY.has(m.key) || DEFAULT_ON.has(m.key),
}));
const SELECTABLE = TEST_HOSPITAL_MODULE_CHOICES.map((m) => m.key);
const PersonName = z
    .string()
    .trim()
    .min(1)
    .max(60)
    .regex(/^[\p{L}\p{M}' .-]+$/u, 'Letters, spaces, hyphens and apostrophes only');
const TesterEmail = z
    .email()
    .transform((v) => v.trim().toLowerCase())
    .refine((v) => v.endsWith(`@${TESTER_EMAIL_DOMAIN}`), {
    message: `Test hospitals can only be made for a @${TESTER_EMAIL_DOMAIN} address.`,
});
/** What the owner submits. */
export const CreateTestHospitalRequestSchema = z
    .object({
    firstName: PersonName,
    lastName: PersonName,
    testerEmail: TesterEmail,
    hospitalName: z.string().trim().min(3).max(120),
    modules: z
        .array(z.enum(SELECTABLE))
        .max(SELECTABLE.length)
        .transform((keys) => {
        // Mandatory modules are always on, whatever the form sent.
        const set = new Set([...TEST_HOSPITAL_MANDATORY_MODULES, ...keys]);
        return TENANT_MODULE_KEYS.filter((k) => set.has(k));
    }),
})
    .strict();
/** The signed command payload. */
export const CreateTestHospitalPayloadSchema = z
    .object({
    tenantOrganisationId: z.uuid(),
    firstName: PersonName,
    lastName: PersonName,
    testerEmail: TesterEmail,
    hospitalName: z.string().trim().min(3).max(120),
    modules: z.array(z.enum(SELECTABLE)).min(TEST_HOSPITAL_MANDATORY_MODULES.length),
})
    .strict();
//# sourceMappingURL=test-hospital.js.map